import React, { useEffect, useState } from "react";
import { Button, Form } from "antd";
import UploadOfflineFile from "./UploadOflineFile";
import Kit from "./Kit";
import { useDispatch, useSelector } from "react-redux";
import {
  AppDispatch,
  decryptOfflineReadingRequest,
  getKitListRequest,
  uploadOfflineReadingRequest,
  Organization,
  decryptOfflineReading,
  uploadOfflineReading,
} from "@src/redux";
import toast from "react-hot-toast";
import type { UploadFile } from "antd/es/upload/interface";

const DEFAULT_LIMIT_COUNT = 10;
interface UploadOfflineReadingFormProps {
  onCancel: Function;
  client: Organization;
}

const UploadOfflineReadingForm: React.FC<UploadOfflineReadingFormProps> = ({
  onCancel,
  client,
}) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch<AppDispatch>();
  const { loading: decryptLoading } = useSelector(decryptOfflineReading);
  const { loading: uploadLoading } = useSelector(uploadOfflineReading);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (values: any) => {
    const fileList: UploadFile[] = values?.offlineFile || [];
    const fileItem = fileList?.[0];
  
    if (!fileItem) {
      toast.error("Offline file is required.");
      return;
    }
  
    const uploadedFilename = fileItem.name;
    const uploadedFileUrl = fileItem.url;

    if (!uploadedFilename || !uploadedFileUrl) {
      toast.error("Uploaded file information is missing.");
      return;
    }

    const kitId = values?.kit;
    if (!kitId) {
      toast.error("Kit is required.");
      return;
    }

    // Prefer the S3 key the backend returned in the getSignedUrl response
    // (stored on the file object by handleUpload). Fall back to parsing
    // from the URL only when s3Key is missing (e.g. legacy callers). Note
    // the legacy split(".com/") fallback is wrong for path-style URLs —
    // it includes the bucket name as a prefix in the extracted key — but
    // we keep it as a last-resort for back-compat.
    const fileName: string =
      (fileItem as any)?.s3Key ||
      uploadedFileUrl?.split(".com/")?.[1] ||
      "";

    if (!fileName) {
      toast.error("Uploaded file information is missing.");
      return;
    }
  
    try {
      setSubmitting(true);
  
      const decryptResponse = await dispatch(
        decryptOfflineReadingRequest({
          data: {
            fileName: fileName, // Pass string here, not array
            organizationId: client?.id,
          },
        })
      ).unwrap();
  
      if (decryptResponse?.error?.message) {
        throw new Error(decryptResponse.error.message);
      }
  
      const readingCount = decryptResponse?.readingCount || 0;
      if (!readingCount) {
        toast.success("No new readings found in the uploaded file.");
        form.resetFields();
        onCancel?.();
        return;
      }
  
      const totalIterations = Math.ceil(readingCount / DEFAULT_LIMIT_COUNT);
      let skipCount = 0;
      let addedReadings: string[] = [];
      let duplicateReadings: string[] = [];
      let errorReadings: { id: string; remark: string }[] = [];

      for (let index = 1; index <= totalIterations; index++) {
        const uploadResponse = await dispatch(
          uploadOfflineReadingRequest({
            data: {
              kitId: kitId,
              fileName: fileName,
              skipCount: skipCount,
              limitCount: DEFAULT_LIMIT_COUNT,
              shouldDelete: index === totalIterations,
              organizationId:client?.id
            },
          })
        ).unwrap();

        if (uploadResponse?.error?.message) {
          throw new Error(uploadResponse.error.message);
        }

        addedReadings = [
          ...addedReadings,
          ...(uploadResponse?.addedReadings || []),
        ];
        duplicateReadings = [
          ...duplicateReadings,
          ...(uploadResponse?.duplicateReadings || []),
        ];
        errorReadings = [
          ...errorReadings,
          ...(uploadResponse?.errorReadings || []),
        ];
        skipCount += DEFAULT_LIMIT_COUNT;
      }

      // Honest outcome reporting: previously this always showed
      // "uploaded successfully" even when every reading hit errorReadings,
      // which is what made the "upload works but nothing shows up" bug
      // invisible. Branch on what the BL actually returned.
      const addedCount = addedReadings.length;
      const duplicateCount = duplicateReadings.length;
      const errorCount = errorReadings.length;

      if (addedCount === 0 && errorCount > 0) {
        const firstRemark = errorReadings[0]?.remark || "unknown error";
        toast.error(
          `Upload failed: ${errorCount} reading${errorCount === 1 ? "" : "s"} rejected (${firstRemark})`
        );
      } else if (errorCount > 0) {
        toast(
          `Added ${addedCount}, duplicate ${duplicateCount}, ${errorCount} rejected`,
          { icon: "⚠️" }
        );
      } else if (addedCount === 0 && duplicateCount > 0) {
        toast.success(`All ${duplicateCount} reading${duplicateCount === 1 ? "" : "s"} already exist (duplicate)`);
      } else {
        toast.success(
          duplicateCount > 0
            ? `Added ${addedCount}, ${duplicateCount} duplicate`
            : `Offline Readings uploaded successfully (${addedCount})`
        );
      }
      form.resetFields();
      onCancel?.();
    } catch (error: any) {
      const message =
        error?.message ||
        error?.statusCode ||
        "Failed to upload offline readings.";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };
  
  useEffect(() => {
    dispatch(
      getKitListRequest({
        filter: { organizationId: client.id },
        sort: [{ field: "referenceId", order: "asc" }],
      })
    );
  }, [client.id, dispatch]);

  return (
    <Form layout="vertical" onFinish={handleSubmit} form={form}>
      <UploadOfflineFile form={form} />
      <Kit />
      <div className="offline_reading_buttons">
        <Button onClick={() => onCancel()} danger>
          Close
        </Button>
        <Button
          type="primary"
          htmlType="submit"
          loading={submitting || decryptLoading || uploadLoading}
        >
          Upload
        </Button>
      </div>
    </Form>
  );
};

export default UploadOfflineReadingForm;
